import UsageEventsApi from '../../services/api/UsageEventsApi';

export const ONBOARDING_EVENTS = [
  'ONBOARDING_STARTED',
  'ONBOARDING_STEP_COMPLETED',
  'ONBOARDING_STEP_SKIPPED',
  'ONBOARDING_STEP_VIEWED',
  'ONBOARDING_STEP_DURATION',
  'ONBOARDING_COMPLETED',
  'ONBOARDING_ABANDONED',
];

export const SCAN_EVENTS = [
  'ONBOARDING_SCAN',
  'ONBOARDING_SCAN_VIDEO',
];

export const INTERACTION_EVENTS = ['ONBOARDING_INTERACTION'];

// Canonical step order – used to build the funnel.
// Per-course dynamic steps (course-review-N, docs-upload-N) are grouped.
export const STEP_ORDER = [
  { id: 'welcome',         label: 'Welcome' },
  { id: 'profile',         label: 'Profile' },
  { id: 'syllabus-upload', label: 'Syllabus Upload' },
  { id: 'scan-wait',       label: 'Processing' },
  { id: 'course-review',   label: 'Course Review' },
  { id: 'docs-upload',     label: 'Docs Upload' },
  { id: 'ai-tools',        label: 'AI Tools' },
  { id: 'complete',        label: 'Complete' },
];

export const CHART_COLORS = ['#4a90e2', '#50c878', '#f5a623', '#e94b3c', '#9b59b6', '#1abc9c', '#e74c3c', '#3498db', '#f39c12', '#2ecc71'];

/** Normalise per-course step IDs (e.g. "course-review-2") into their base group */
export function normalizeStepId(raw) {
  if (!raw) return raw;
  if (raw.startsWith('course-review')) return 'course-review';
  if (raw.startsWith('docs-upload'))   return 'docs-upload';
  return raw;
}

export function parsePayload(e) {
  try {
    return typeof e.payloadJson === 'string' ? JSON.parse(e.payloadJson) : (e.payloadJson || e.payload || {});
  } catch {
    return {};
  }
}

export async function fetchEventsByType(eventType, from, to) {
  const pageSize = 1000;
  let page = 0;
  let totalPages = 1;
  const collected = [];

  const maxPages = 10;
  while (page < totalPages && page < maxPages && collected.length < 10000) {
    const response = await UsageEventsApi.adminSearch({ eventType, from, to, page, size: pageSize });
    if (!response.ok) throw new Error(`adminSearch failed for ${eventType}: ${response.status}`);
    const data = await response.json();
    const content = Array.isArray(data) ? data : (data.content || data.items || data.results || []);
    collected.push(...content);
    totalPages = Number.isInteger(data.totalPages) ? data.totalPages : (content.length < pageSize ? page + 1 : page + 2);
    page += 1;
  }
  return collected;
}

export function formatDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}m ${sec}s`;
}

// ── Client-side processing ───────────────────────────────────────────

export function computeOverviewStats(started, completed, abandoned) {
  const totalStarted = started.length;
  const totalCompleted = completed.length;
  const totalAbandoned = abandoned.length;
  const completionRate = totalStarted > 0 ? ((totalCompleted / totalStarted) * 100) : 0;
  const abandonmentRate = totalStarted > 0 ? ((totalAbandoned / totalStarted) * 100) : 0;

  let avgDurationMs = 0;
  const durations = completed
    .map(e => {
      const p = parsePayload(e);
      return p.metadata?.totalDurationMs || p.totalDurationMs;
    })
    .filter(Boolean);
  if (durations.length) avgDurationMs = durations.reduce((a, b) => a + b, 0) / durations.length;

  return { totalStarted, totalCompleted, totalAbandoned, completionRate, abandonmentRate, avgDurationMs };
}

export function computeStepFunnel(viewedEvents) {
  const stepCounts = new Map();
  viewedEvents.forEach(e => {
    const p = parsePayload(e);
    const stepId = normalizeStepId(p.entityId || p.label || 'unknown');
    const actor = e.userId || e.sessionId || 'anon';
    if (!stepCounts.has(stepId)) stepCounts.set(stepId, new Set());
    stepCounts.get(stepId).add(actor);
  });

  return STEP_ORDER.map(s => {
    const users = stepCounts.get(s.id)?.size || 0;
    return { step: s.label, users };
  }).filter(s => s.users > 0 || STEP_ORDER.indexOf(STEP_ORDER.find(o => o.label === s.step)) < 4);
}

export function computeStepDropoff(funnelData) {
  return funnelData.map((item, idx) => {
    const prev = idx > 0 ? funnelData[idx - 1].users : item.users;
    const dropoff = prev > 0 ? (((prev - item.users) / prev) * 100) : 0;
    return { ...item, dropoff: Math.round(dropoff * 10) / 10 };
  });
}

export function computeStepDurations(durationEvents) {
  const stepDurations = new Map();
  durationEvents.forEach(e => {
    const p = parsePayload(e);
    const stepId = normalizeStepId(p.label || 'unknown');
    const ms = p.value || 0;
    if (ms <= 0) return;
    if (!stepDurations.has(stepId)) stepDurations.set(stepId, []);
    stepDurations.get(stepId).push(ms);
  });

  return STEP_ORDER
    .map(s => {
      const vals = stepDurations.get(s.id) || [];
      if (!vals.length) return null;
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      return { step: s.label, avgSeconds: Math.round(avg / 1000) };
    })
    .filter(Boolean);
}

export function computeTrends(startedEvents, completedEvents) {
  const dayMap = new Map();
  const tally = (events, key) => {
    events.forEach(e => {
      const ts = e.occurredAt || e.createdAt || e.timestamp;
      if (!ts) return;
      const day = new Date(ts).toISOString().split('T')[0];
      if (!dayMap.has(day)) dayMap.set(day, { date: day, started: 0, completed: 0 });
      dayMap.get(day)[key]++;
    });
  };
  tally(startedEvents, 'started');
  tally(completedEvents, 'completed');
  return Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export function computeModeBreakdown(startedEvents) {
  let manual = 0;
  let syllabus = 0;
  startedEvents.forEach(e => {
    const p = parsePayload(e);
    const mode = p.metadata?.mode || p.mode;
    if (mode === 'manual') manual++;
    else syllabus++;
  });
  const data = [];
  if (syllabus) data.push({ name: 'Syllabus Upload', value: syllabus });
  if (manual) data.push({ name: 'Manual Entry', value: manual });
  return data;
}

export function computeSkippedSteps(skippedEvents) {
  const counts = new Map();
  skippedEvents.forEach(e => {
    const p = parsePayload(e);
    const stepId = normalizeStepId(p.label || 'unknown');
    counts.set(stepId, (counts.get(stepId) || 0) + 1);
  });
  const lookup = Object.fromEntries(STEP_ORDER.map(s => [s.id, s.label]));
  return Array.from(counts.entries())
    .map(([id, count]) => ({ step: lookup[id] || id, count }))
    .sort((a, b) => b.count - a.count);
}

export function computeScanPerformance(scanCompleted, scanErrors, scanTimeouts) {
  const allDurations = [];
  const allWaitDurations = [];

  scanCompleted.forEach(e => {
    const p = parsePayload(e);
    const d = p.metadata?.scanDurationMs;
    const w = p.metadata?.totalWaitDurationMs;
    if (d && d > 0) allDurations.push(d);
    if (w && w > 0) allWaitDurations.push(w);
  });

  const totalScans = scanCompleted.length + scanErrors.length + scanTimeouts.length;
  const avgScanDurationMs = allDurations.length > 0
    ? allDurations.reduce((a, b) => a + b, 0) / allDurations.length : 0;
  const sortedDurations = [...allDurations].sort((a, b) => a - b);
  const medianScanDurationMs = sortedDurations.length > 0
    ? sortedDurations[Math.floor(sortedDurations.length / 2)] : 0;
  const avgWaitDurationMs = allWaitDurations.length > 0
    ? allWaitDurations.reduce((a, b) => a + b, 0) / allWaitDurations.length : 0;
  const scanErrorRate = totalScans > 0
    ? ((scanErrors.length + scanTimeouts.length) / totalScans * 100) : 0;

  const buckets = [
    { label: '<10s', min: 0, max: 10000 },
    { label: '10-30s', min: 10000, max: 30000 },
    { label: '30s-1m', min: 30000, max: 60000 },
    { label: '1-2m', min: 60000, max: 120000 },
    { label: '2-5m', min: 120000, max: 300000 },
    { label: 'Timeout', min: 300000, max: Infinity },
  ];
  const histogram = buckets.map(b => ({
    bucket: b.label,
    count: allDurations.filter(d => d >= b.min && d < b.max).length,
  }));

  const dayMap = new Map();
  scanCompleted.forEach(e => {
    const ts = e.occurredAt || e.createdAt;
    if (!ts) return;
    const day = new Date(ts).toISOString().split('T')[0];
    const p = parsePayload(e);
    const d = p.metadata?.scanDurationMs;
    if (!d) return;
    if (!dayMap.has(day)) dayMap.set(day, { date: day, totalMs: 0, count: 0 });
    const entry = dayMap.get(day);
    entry.totalMs += d;
    entry.count++;
  });
  const dailyTrend = Array.from(dayMap.values())
    .map(d => ({ date: d.date, avgSeconds: Math.round(d.totalMs / d.count / 1000) }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    totalScans, avgScanDurationMs, medianScanDurationMs, avgWaitDurationMs,
    scanErrorRate, scanErrorCount: scanErrors.length + scanTimeouts.length, histogram, dailyTrend,
  };
}

export function computeVideoEngagement(videoSelected, videoDuration, scanIdle, scanSkip) {
  const selectionCounts = new Map();
  videoSelected.forEach(e => {
    const p = parsePayload(e);
    const title = p.metadata?.videoTitle || p.metadata?.videoId || 'Unknown';
    selectionCounts.set(title, (selectionCounts.get(title) || 0) + 1);
  });
  const videoSelections = Array.from(selectionCounts.entries())
    .map(([title, count]) => ({ video: title, selections: count }))
    .sort((a, b) => b.selections - a.selections);

  const durationMap = new Map();
  videoDuration.forEach(e => {
    const p = parsePayload(e);
    const title = p.metadata?.videoTitle || 'Unknown';
    const ms = p.metadata?.viewDurationMs || 0;
    if (ms <= 0) return;
    if (!durationMap.has(title)) durationMap.set(title, []);
    durationMap.get(title).push(ms);
  });
  const avgWatchDurations = Array.from(durationMap.entries())
    .map(([title, durations]) => ({
      video: title,
      avgSeconds: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length / 1000),
    }))
    .sort((a, b) => b.avgSeconds - a.avgSeconds);

  const sessionsWatched = new Set();
  const sessionsIdle = new Set();
  const sessionsSkipped = new Set();
  videoSelected.forEach(e => { sessionsWatched.add(e.sessionId || e.userId); });
  scanIdle.forEach(e => { sessionsIdle.add(e.sessionId || e.userId); });
  scanSkip.forEach(e => { sessionsSkipped.add(e.sessionId || e.userId); });

  const waitBehavior = [];
  const watchedOnly = [...sessionsWatched].filter(s => !sessionsIdle.has(s) && !sessionsSkipped.has(s));
  if (watchedOnly.length) waitBehavior.push({ name: 'Engaged (watched videos)', value: watchedOnly.length });
  if (sessionsIdle.size) waitBehavior.push({ name: 'Idle (no interaction)', value: sessionsIdle.size });
  if (sessionsSkipped.size) waitBehavior.push({ name: 'Skipped scan', value: sessionsSkipped.size });

  return { videoSelections, avgWatchDurations, waitBehavior, skipCount: scanSkip.length };
}

export function computeStepInteractions(interactionEvents) {
  const stepActions = new Map();
  interactionEvents.forEach(e => {
    const p = parsePayload(e);
    const step = p.label || p.metadata?.step || 'unknown';
    const action = p.action || 'unknown';
    if (!stepActions.has(step)) stepActions.set(step, new Map());
    const actionMap = stepActions.get(step);
    actionMap.set(action, (actionMap.get(action) || 0) + 1);
  });

  return Array.from(stepActions.entries()).map(([step, actionMap]) => ({
    step,
    actions: Array.from(actionMap.entries())
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count),
    totalInteractions: Array.from(actionMap.values()).reduce((a, b) => a + b, 0),
  })).sort((a, b) => b.totalInteractions - a.totalInteractions);
}

export function computeUserJourneys(allEvents) {
  const byActor = new Map();
  allEvents.forEach(ev => {
    const actor = ev.userId || ev.sessionId || 'unknown';
    if (!byActor.has(actor)) byActor.set(actor, []);
    byActor.get(actor).push(ev);
  });

  const journeys = [];
  byActor.forEach((events, actor) => {
    events.sort((a, b) => new Date(a.occurredAt) - new Date(b.occurredAt));

    let status = 'in-progress';
    const stepsVisited = [];
    const stepsSet = new Set();
    let startedAt = null;
    let completedAt = null;
    let userId = null;
    let sessionId = null;

    events.forEach(ev => {
      if (ev.userId) userId = ev.userId;
      if (ev.sessionId) sessionId = ev.sessionId;
      if (!startedAt) startedAt = ev.occurredAt;

      const p = parsePayload(ev);
      if (ev.eventType === 'ONBOARDING_COMPLETED') {
        status = 'completed';
        completedAt = ev.occurredAt;
      } else if (ev.eventType === 'ONBOARDING_ABANDONED') {
        status = 'abandoned';
      }

      if (ev.eventType === 'ONBOARDING_STEP_VIEWED') {
        const stepId = normalizeStepId(p.entityId || p.label || '');
        if (stepId && !stepsSet.has(stepId)) {
          stepsSet.add(stepId);
          stepsVisited.push(stepId);
        }
      }
    });

    const totalDurationMs = startedAt && completedAt
      ? new Date(completedAt) - new Date(startedAt)
      : startedAt
        ? new Date(events[events.length - 1].occurredAt) - new Date(startedAt)
        : 0;

    const lastStepReached = stepsVisited.length > 0 ? stepsVisited[stepsVisited.length - 1] : null;
    const lastStepLabel = STEP_ORDER.find(s => s.id === lastStepReached)?.label || lastStepReached || '—';

    journeys.push({
      actor, userId, sessionId, status, startedAt, completedAt,
      totalDurationMs, stepsVisited, lastStepReached, lastStepLabel,
      eventCount: events.length, events,
    });
  });

  const statusOrder = { 'in-progress': 0, 'abandoned': 1, 'completed': 2 };
  journeys.sort((a, b) => {
    const so = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
    if (so !== 0) return so;
    return new Date(b.startedAt) - new Date(a.startedAt);
  });

  return journeys;
}
